import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { PatchProgramsIdMutationRequest } from '@eureca/recruiter-api-sdk/types';
import { withNormalizedApiDates } from '@/lib/api-dates';
import { programByIdQueryKey } from './program-by-id-query';

// ---------------------------------------------------------------------------
// Shared error type
// ---------------------------------------------------------------------------

/**
 * Domain codes the BFF emits for typed 4xx responses:
 *   - `'duplicate_program_name'` — RN-03 server-side gate (create/edit/publish).
 *   - `'incomplete_jobs'` — EU-450 publish gate: the program has linked jobs
 *     missing required sections. The dialog pre-check normally blocks this
 *     upfront; the code covers the race where a job regressed between the
 *     check and the PATCH.
 */
export type ProgramMutationErrorCode = 'duplicate_program_name' | 'incomplete_jobs';

export class ProgramMutationError extends Error {
  readonly name = 'ProgramMutationError';
  /**
   * Domain code the BFF emits for typed 4xx responses. Lets the SPA render
   * the AC-literal message without re-mapping HTTP codes.
   */
  readonly code: ProgramMutationErrorCode | null;
  constructor(
    message: string,
    readonly status: number,
    code: ProgramMutationErrorCode | null = null
  ) {
    super(message);
    this.code = code;
  }
}

/** Narrow a fetchClient error body into the typed code, when present. */
function extractMutationErrorCode(errBody: unknown): ProgramMutationErrorCode | null {
  if (errBody && typeof errBody === 'object') {
    // Core attaches the machine-readable code via `AppError.details.code`
    // (serialized as `code`); the `error` field mirrors it for legacy shapes.
    const body = errBody as { error?: unknown; code?: unknown };
    const value = body.code ?? body.error;
    if (value === 'duplicate_program_name') return 'duplicate_program_name';
    if (value === 'incomplete_jobs') return 'incomplete_jobs';
  }
  return null;
}

/**
 * Pull the API-supplied `message` out of a fetchClient error body so the SPA
 * can render the wording the backend chose (e.g. the RN-03 duplicate-program
 * literal) instead of a generic fallback.
 */
function extractMutationErrorMessage(errBody: unknown): string | null {
  if (errBody && typeof errBody === 'object' && 'message' in errBody) {
    const value = (errBody as { message?: unknown }).message;
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

// ---------------------------------------------------------------------------
// usePatchProgram
// ---------------------------------------------------------------------------

export function usePatchProgram(programId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchProgramsIdMutationRequest) => {
      const res = await fetchClient({
        method: 'PATCH',
        url: `/programs/${programId}`,
        // EU-402 RN-01/RN-03 — normalize startApplying/endApplying to ISO 8601
        // before sending; without this, dates hydrated from an API read would go
        // back verbatim and the mutation would break with 400 FST_ERR_VALIDATION.
        data: withNormalizedApiDates(patch),
      });
      if (res.error) {
        // BFF's `mapProgramsGatewayError` turns core's technical English
        // messages into pt-BR envelopes carrying a user-facing `message`
        // — surface that on `err.message` so containers can toast it
        // directly without any client-side localization.
        const apiMessage = extractMutationErrorMessage(res.error.error);
        throw new ProgramMutationError(
          apiMessage ?? `Failed to update program (status ${res.error.status})`,
          res.error.status,
          extractMutationErrorCode(res.error.error)
        );
      }
      return res.data;
    },
    onSuccess: () => {
      // Invalidate the by-id query so the next read fetches the JOIN-enriched response
      // (the PATCH response doesn't include `statusKey`). Also invalidates the list so
      // any name/status change is reflected there too.
      void queryClient.invalidateQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteProgram
// ---------------------------------------------------------------------------

export function useDeleteProgram(programId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetchClient({
        method: 'DELETE',
        url: `/programs/${programId}`,
      });
      if (res.error) {
        throw new ProgramMutationError(
          `Failed to delete program (status ${res.error.status})`,
          res.error.status
        );
      }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useUploadProgramCover
// ---------------------------------------------------------------------------

export interface UploadProgramCoverVariables {
  programId: string;
  file: File;
}

export interface UploadProgramCoverResponse {
  coverUrl: string;
}

/**
 * Upload a cover image to the BFF (`POST /programs/:id/cover`). The BFF
 * resizes via sharp, writes the WebP to S3 under
 * `programs/<id>/cover/<uuid>.webp`, calls Core's `PATCH /programs/:id/cover`
 * with the new key, and deletes the previous object. Returns the resolved
 * public URL the FE can render immediately.
 */
export function useUploadProgramCover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      file,
    }: UploadProgramCoverVariables): Promise<UploadProgramCoverResponse> => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const res = await fetchClient<UploadProgramCoverResponse>({
        method: 'POST',
        url: `/programs/${programId}/cover`,
        data: formData,
      });
      if (res.error) {
        throw new ProgramMutationError(
          `Failed to upload program cover (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: (_data, { programId }) => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useClearProgramCover
// ---------------------------------------------------------------------------

/**
 * Clear the cover from a program (`DELETE /programs/:id/cover`). The BFF
 * writes a sentinel to Core's `cover_key` (Core's HTTP API has no null-clear
 * for that column today) and deletes the S3 object so the bucket no longer
 * holds the file. The next `GET /programs/:id` filters the sentinel back to
 * `null`, so the SPA renders an empty cover state again.
 */
export function useClearProgramCover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string): Promise<void> => {
      const res = await fetchClient({
        method: 'DELETE',
        url: `/programs/${programId}/cover`,
      });
      if (res.error) {
        throw new ProgramMutationError(
          `Failed to clear program cover (status ${res.error.status})`,
          res.error.status
        );
      }
    },
    onSuccess: (_data, programId) => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// usePublishProgram / useCancelProgram
// ---------------------------------------------------------------------------

/**
 * Reference status IDs used by status-transition mutations.
 *
 * Sourced from `core/backend/seeds/reference-tables/status.json`. The list
 * endpoint joins on `status.key`, so the FE never reads these ids back — it
 * only writes them. If the seed shifts, update both Core and this constant.
 */
const STATUS_ID_PRIVATE = 2;
const STATUS_ID_PUBLISHED = 3;
const STATUS_ID_CANCELLED = 4;
const STATUS_ID_INACTIVATED = 5;

export type PublishVisibility = 'public' | 'private';

export interface PublishProgramVariables {
  programId: string;
  visibility: PublishVisibility;
}

/**
 * Publish a program — flips the status to `published` (público) or `private`
 * (privado) and stamps `publishedAt`. Visibilidade pública é derivada de
 * `status_id ∈ (2, 3)` no Core (a coluna `is_active` foi colapsada). Reused
 * by both the publish dialog (público/privado choice) and the row-action
 * "Privar" shortcut on already-public programs.
 */
export function usePublishProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ programId, visibility }: PublishProgramVariables) => {
      const statusId = visibility === 'public' ? STATUS_ID_PUBLISHED : STATUS_ID_PRIVATE;
      const res = await fetchClient({
        method: 'PATCH',
        url: `/programs/${programId}`,
        data: {
          statusId,
          publishedAt: new Date().toISOString(),
        },
      });
      if (res.error) {
        const apiMessage = extractMutationErrorMessage(res.error.error);
        throw new ProgramMutationError(
          apiMessage ?? `Failed to publish program (status ${res.error.status})`,
          res.error.status,
          extractMutationErrorCode(res.error.error)
        );
      }
      return res.data;
    },
    onSuccess: (_data, { programId }) => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

/**
 * Cancel a program: flip status to `cancelled`. A coluna `is_active` foi
 * colapsada em `status_id` — `cancelled` (id 4) já implica "não visível
 * publicamente". Takes the program id at call time (rows in the listing
 * have varying ids), unlike `usePatchProgram` which binds a single id at
 * hook time.
 */
export function useCancelProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const res = await fetchClient({
        method: 'PATCH',
        url: `/programs/${programId}`,
        data: { statusId: STATUS_ID_CANCELLED },
      });
      if (res.error) {
        throw new ProgramMutationError(
          `Failed to cancel program (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: (_data, programId) => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

/**
 * End ("Encerrar" / "Concluir") a program: flip status to `inactivated`
 * (status_id 5). The `is_active` column was collapsed into `status_id`, so
 * `inactivated` already implies the program is no longer publicly visible and
 * accepts no new applications. Takes the program id at call time so the same
 * hook serves both the listing row action and the edit-shell header menu.
 */
export function useEndProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const res = await fetchClient({
        method: 'PATCH',
        url: `/programs/${programId}`,
        data: { statusId: STATUS_ID_INACTIVATED },
      });
      if (res.error) {
        throw new ProgramMutationError(
          `Failed to end program (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    onSuccess: (_data, programId) => {
      queryClient.removeQueries({ queryKey: programByIdQueryKey(programId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useDuplicateProgram
// ---------------------------------------------------------------------------

export function useDuplicateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (srcId: string) => {
      const res = await fetchClient<{ id: string }>({
        method: 'POST',
        url: `/programs/${srcId}/duplicate`,
        // Empty `{}` so Fastify's JSON parser doesn't reject the request with
        // FST_ERR_CTP_EMPTY_JSON_BODY. The BFF route doesn't declare a body
        // schema, so the payload is ignored server-side.
        data: {},
      });
      if (res.error) {
        const apiMessage = extractMutationErrorMessage(res.error.error);
        throw new ProgramMutationError(
          apiMessage ?? `Failed to duplicate program (status ${res.error.status})`,
          res.error.status,
          extractMutationErrorCode(res.error.error)
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
    },
  });
}
